import {Component, OnInit, OnDestroy, OnChanges, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';

/**
* AI:
* Status: 'in progress'
* Type: Service
* SubType: WidgetConfig
* Reason: Configured data provider for widget data fetching
*/

import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {PagingDto} from 'features/core/shared/dto/model/paging.dto';
import {DtoList} from 'features/core/shared/dto/dto.list';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {CnReconciliationApiService} from '../../../service/api/cn-reconciliation-api/cn-reconciliation-api.service';
import {CnCreateStatementRequestDto} from '../../../model/dto/api/cn-create-statement-request.dto';
import {CnAddStatementLineRequestDto} from '../../../model/dto/api/cn-add-statement-line-request.dto';
import {CnRunReconciliationRequestDto} from '../../../model/dto/api/cn-run-reconciliation-request.dto';
import {CnReconciliationResultDto} from '../../../model/dto/api/cn-reconciliation-result.dto';
import {CommissionStatementDto} from '../../../model/dto/entity/commission-statement/commission-statement.dto';
import {
    CommissionStatementLineDto
} from '../../../model/dto/entity/commission-statement-line/commission-statement-line.dto';
import {cnErrorMessage} from '../../../shared/cn-error-message.util';

/**
* Reconciliation orchestration panel (P3 / FR-Abgleich), hosted by CnReconciliationPage.
*
* Three workflows, all driven by CnReconciliationApiService (NO calculation in the FE —
* all amounts/deltas/statuses come from the backend):
*
*  1. »Empfangene Provisionen erfassen«: action inputs (partner, Stichtag, Periode) create a
*     CommissionStatement (createStatement), then per-line action inputs (VSNR, Tarif,
*     Buchungsperiode, Betrag, Trigger NEUZUGANG/STORNO, contractId) append lines (addLine).
*     These are NOT entity CRUD forms — they are simple PrimeNG action inputs.
*
*  2. »Abgleich starten«: runReconciliation(partnerId, period) returns a per-status summary,
*     then a SERVER-PAGED mvs-widget on cn.CommissionReconciliation is rendered (status badge
*     from enum uiColor, expectedCombinedAmount, amountDelta, expectationGroupKey). Selecting a
*     row drills down to its cn.CommissionReconciliationComponent rows in a second server-paged
*     widget.
*
*  3. »Modell-Abgleich«: abgleichVsModels(statementId, activeModelId, scenarioModelIds,
*     horizonYears) renders received-vs-each-model expected amounts plus the delta per period.
*
* UI states (FR-16): idle / busy / ready / empty / error — for each workflow independently.
*/
@Component({
    selector: 'mvs-cn-reconciliation-panel',
    templateUrl: './cn-reconciliation-panel.component.html',
    styleUrls: ['./cn-reconciliation-panel.component.scss'],
    standalone: false
})
export class CnReconciliationPanelComponent implements OnInit, OnDestroy, OnChanges {

    /** Server-paged reconciliation result table (master). */
    reconciliationWidget: WidgetData | null = null;
    /** Server-paged drill-down: components of the selected reconciliation row (detail). */
    componentWidget: WidgetData | null = null;

    // ===== Statement-erfassen action inputs =====
    statementPartnerId: number | null = null;
    statementDate: Date | null = null;
    /**
     * FE-8: the Periode is chosen with the SAME month picker as the dashboard (p-datePicker
     * view="month", dd.mm.yy) instead of a free-text "JJJJ-MM" field. This Date is the picker
     * model; it is serialised to a YearMonth "YYYY-MM" string via periodString() for the API.
     */
    statementPeriodDate: Date | null = null;

    /** Created statement (after createStatement); enables line entry + Modell-Abgleich. */
    statement: CommissionStatementDto | null = null;
    /** Lines appended to the created statement, kept for the entry log. */
    statementLines: CommissionStatementLineDto[] = [];

    statementState: 'idle' | 'busy' | 'ready' | 'error' = 'idle';
    statementError: string | null = null;

    // ===== Statement-line action inputs =====
    lineVsnr: string | null = null;
    lineTariffKey: string | null = null;
    lineBookingPeriod: string | null = null;
    lineAmount: number | null = null;
    /**
     * TM-1000001915 (B11): trigger is the AlphaBaseEnum ordinal (0 = Neuzugang, 1 = Storno),
     * not the string name — the backend rejects the string with HTTP 400.
     */
    lineTrigger: number = 0;
    lineContractId: number | null = null;

    lineState: 'idle' | 'busy' | 'error' = 'idle';
    lineError: string | null = null;

    /** Trigger options for the line entry (Neuzugang / Storno) — values are the enum ordinals. */
    readonly triggerOptions = [
        {label: 'Neuzugang', value: 0},
        {label: 'Storno', value: 1}
    ];

    /**
     * Plain-German legend for the 7 CommissionReconciliationStatus values (Simone W4 / FE-8). Shown
     * as a legend near the result table and as tooltips. The result table itself renders the backend
     * enum uiLabel (German); the legend `label` MUST be byte-for-byte the SAME wording as that
     * uiLabel so the user can map a badge in the table to a legend entry without guessing
     * (e.g. "Unterzahlung"/"Überzahlung", NOT "Unter"/"Über"). The `description` explains the status
     * in everyday language. Source of truth: CommissionReconciliationStatus.uiLabel (backend enum).
     */
    readonly reconciliationStatusLegend: { label: string; description: string }[] = [
        {label: 'In Ordnung', description: 'Empfangener Betrag stimmt mit der Erwartung überein.'},
        {label: 'Unterzahlung', description: 'Empfangener Betrag liegt unter der Erwartung.'},
        {label: 'Überzahlung', description: 'Empfangener Betrag liegt über der Erwartung.'},
        {label: 'Fehlend', description: 'Erwartete Provision wurde nicht empfangen.'},
        {label: 'Unerwartet', description: 'Provision empfangen, zu der keine Erwartung vorliegt.'},
        {label: 'Manuell', description: 'Manuell geprüfter bzw. zugeordneter Vorgang.'},
        {label: 'Bonus', description: 'Zusätzliche Provision über die Erwartung hinaus (Bonus).'}
    ];

    /**
     * Partner options for the Partner-Auswahl (Simone W5): selectable by NAME instead of typing
     * raw ids. Loaded from the CONFIGURED commission partners only (cn.CommissionPartner joined N:1
     * to its cm.Partner relation) — we do not receive commissions from every partner, so this is a
     * much smaller, configured set. value is the partner id, label is the partner name.
     */
    partnerOptions: { label: string; value: number }[] = [];

    /**
     * Beleg (statement) options for the Modell-Abgleich Beleg-Auswahl (Simone W5): selectable by
     * a readable label "Beleg #id — Partner, Periode" instead of typing the raw statement id.
     */
    statementOptions: { label: string; value: number }[] = [];

    // ===== Abgleich (runReconciliation) action inputs =====
    runPartnerId: number | null = null;
    /**
     * FE-8: the Abgleich Periode is also chosen with the dashboard month picker (Date model),
     * serialised to "YYYY-MM" for the API via periodString().
     */
    runPeriodDate: Date | null = null;

    runState: 'idle' | 'busy' | 'ready' | 'error' = 'idle';
    runError: string | null = null;
    runResult: CnReconciliationResultDto | null = null;

    // ===== Modell-Abgleich (abgleichVsModels) action inputs =====
    abgleichStatementId: number | null = null;
    abgleichActiveModelId: number | null = null;
    abgleichScenarioModelIds: number[] = [];
    abgleichHorizonYears: number = 5;

    /** Picker widgets for the active / scenario models (metadata-driven selectable lists). */
    abgleichActiveModelWidget: WidgetData | null = null;
    abgleichScenarioModelWidget: WidgetData | null = null;

    initialized: boolean = false;

    constructor(
        private reconciliationApiService: CnReconciliationApiService,
        private coreService: MvsCoreService,
        private qlService: QlService,
        private router: Router
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
    * React to bound input changes. The panel is currently self-contained (no @Input bindings);
    * once initialized it rebuilds the model picker widgets, preserving the contract for any
    * future page-supplied context inputs without forcing a rebuild during first init.
    */
    ngOnChanges(changes: SimpleChanges): void {
        if (this.initialized) {
            this.refreshModelWidgets();
        }
    }

    /**
    * Initialize the panel: build the model picker widgets for the Modell-Abgleich sub-panel and
    * load the selectable partner / model / statement lists (Simone W5) so the user picks by NAME.
    */
    initComponent(): void {
        this.refreshModelWidgets();
        this.loadPartnerOptions();
        this.loadStatementOptions();
        this.initialized = true;
    }

    /**
    * AI: Load the selectable partner list for the Partner-Auswahl dropdowns via QL.
    *
    * Restricted to the CONFIGURED commission partners only: we do not receive commissions from every
    * partner, so the list is a much smaller, configured set. The query therefore starts at
    * cn.CommissionPartner and joins N:1 to its `partner` relation (cm.Partner), reading the joined
    * partner's id + name off the 'p' alias (p.id / p.name) — so the dropdowns show the real partner
    * NAME (Simone W5) while the option value remains the partner id consumed by the API. Rows are
    * de-duplicated by partner id (defensive; there is one CommissionPartner per partner) and the
    * backend already sorts by name (Sorting('p.name', true)). Shape kept as {label, value} so both
    * Partner-Auswahl dropdowns (Beleg-Kopf + Abgleich) keep working unchanged.
    */
    loadPartnerOptions(): void {
        this.qlService.builder()
            .query({
                name: 'cnReconciliationPartnerOptions',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cn.CommissionPartner',
                    as: 'e',
                    // N:1 join to the `partner` relation (cm.Partner); the joined partner's id + name are
                    // read off the 'p' alias (p.id / p.name), so the dropdown shows the real partner name.
                    joins: [
                        {
                            joinType: 'left',
                            type: 'entity',
                            name: 'partner',
                            as: 'p'
                        }
                    ]
                },
                sortings: [
                    new Sorting('p.name', true)
                ],
                paging: PagingDto.createSize(500)
            })
            .execute()
            .subscribe({
                next: (result) => {
                    const rows: any[] = result.get('cnReconciliationPartnerOptions') ?? [];
                    const seen = new Set<number>();
                    const options: { label: string; value: number }[] = [];
                    for (const r of rows) {
                        const id: number = r['p.id'];
                        if (id == null || seen.has(id)) {
                            continue;
                        }
                        seen.add(id);
                        options.push({label: r['p.name'] ?? `Partner #${id}`, value: id});
                    }
                    this.partnerOptions = options;
                },
                error: () => {
                    // Non-fatal: the dropdowns simply stay empty, the existing flow is unaffected.
                }
            });
    }

    /**
    * AI: Load existing CommissionStatements for the Beleg-Auswahl dropdown (Simone W5). The option
    * label reads "Beleg #id — Partner, Periode"; the value is the statement id used by the API.
    */
    loadStatementOptions(): void {
        const statementService = this.coreService.getCrudService('cn.CommissionStatement');
        if (!statementService) {
            return;
        }
        const request = ObjectRequestList.createBasic(true, [], [new Sorting('id', false)]);
        statementService.list(request).subscribe({
            next: (res: DtoList) => {
                this.statementOptions = (res?.entries ?? []).map((s: any) => ({
                    label: this.buildStatementLabel(s.id, s.partnerId, s.period),
                    value: s.id
                }));
            },
            error: () => {
                // Non-fatal: the Beleg-ID input remains usable for manual entry.
            }
        });
    }

    /** Build the readable Beleg label "Beleg #id — Partner, Periode" for the dropdown. */
    private buildStatementLabel(id: number, partnerId?: number, period?: string): string {
        const partnerName = partnerId != null ? this.partnerName(partnerId) : '–';
        const periodText = period ?? '–';
        return `Beleg #${id} — ${partnerName}, ${periodText}`;
    }

    /** Resolve a partner NAME from its id via the loaded partner options (fallback: "Partner #id"). */
    partnerName(partnerId: number): string {
        const match = this.partnerOptions.find(o => o.value === partnerId);
        return match ? match.label : `Partner #${partnerId}`;
    }

    /** Klartext for a statement-line trigger ordinal (0 = Neuzugang, 1 = Storno). */
    triggerLabel(trigger: number): string {
        const match = this.triggerOptions.find(o => o.value === trigger);
        return match ? match.label : '–';
    }

    ngOnDestroy(): void {
    }

    /**
    * AI:
    * Status: 'in progress'
    * Type: Widget
    * SubType: TableWidget (createWidgetTableQl + WidgetForm custom layout)
    * Reason: Build the two selectable CommissionModel picker tables (Aktiv-Modell / Szenario-Modelle)
    *         for the Modell-Abgleich sub-panel. Uses the curated createWidgetTableQl path with an
    *         explicit QL projection + WidgetForm so only business-meaningful columns
    *         (Name / Status / Gültig ab / Gültig bis) are shown — the picker is driven by the model
    *         NAME. The previous createWidgetTableEntityFkeyResolveQl path does NOT honor a WidgetForm
    *         layout (SKILL.md TW-3), so it leaked the entity's default audit columns (Angelegt von,
    *         Sparte-ID, Freigegeben von, Farbe, Quelle, …). Row selection (onObjectSelect) is unchanged.
    */
    refreshModelWidgets(): void {
        this.abgleichActiveModelWidget = WidgetFactory.createWidgetTableQl(
            'cn.reconciliation.abgleich.active.model.widget',
            'Aktiv-Modell',
            CnReconciliationPanelComponent.buildModelPickerQlQuery(
                'cn.reconciliation.abgleich.active.model.widget'
            ),
            WidgetDataParam.create('widgetForm', CnReconciliationPanelComponent.buildModelPickerWidgetForm())
        );
        this.abgleichScenarioModelWidget = WidgetFactory.createWidgetTableQl(
            'cn.reconciliation.abgleich.scenario.model.widget',
            'Szenario-Modelle',
            CnReconciliationPanelComponent.buildModelPickerQlQuery(
                'cn.reconciliation.abgleich.scenario.model.widget'
            ),
            WidgetDataParam.create('widgetForm', CnReconciliationPanelComponent.buildModelPickerWidgetForm())
        );
    }

    /**
    * Explicit QL projection for the CommissionModel picker tables. Only the columns that the curated
    * WidgetForm exposes are selected (root entity `e`); the persisted audit fields are never projected.
    */
    private static buildModelPickerQlQuery(alias: string): QlQueryDto {
        return {
            name: alias,
            type: 'view',
            start: {
                name: 'cn.CommissionModel',
                as: 'e',
                type: 'entity',
                fields: [
                    {name: 'id'},
                    {name: 'name'},      // Name (primary pick key)
                    {name: 'status'},    // Status
                    {name: 'validFrom'}, // Gültig ab
                    {name: 'validTo'},   // Gültig bis
                ],
            },
            fixedFilters: [],
            // e. prefix is mandatory for QL sorting field names on the root content reference.
            sortings: [new Sorting('e.name', true)],
        };
    }

    /**
    * Curated WidgetForm layout for the CommissionModel picker tables. Pins the column SET + ORDER and
    * applies explicit German uiLabels. Field ids are the QL result keys (`e.`-prefixed). hideFields=true
    * is the default-layout convention (fields with uiHidden=false are shown).
    */
    private static buildModelPickerWidgetForm(): WidgetForm {
        const columns: { field: string; uiLabel: string }[] = [
            {field: 'e.name', uiLabel: 'Name'},
            {field: 'e.status', uiLabel: 'Status'},
            {field: 'e.validFrom', uiLabel: 'Gültig ab'},
            {field: 'e.validTo', uiLabel: 'Gültig bis'},
        ];

        const layout = new WidgetConfigFormLayout();
        layout.hideFields = true;
        layout.fieldsConfigured = true;

        columns.forEach((column, index) => {
            const field = WidgetConfigFormField.create(column.field, '', '');
            field.uiLabel = column.uiLabel; // explicit German header
            field.uiPriority = index;       // preserves the curated column order
            layout.addField(field);
        });

        const widgetForm = new WidgetForm();
        widgetForm.setDefaultLayout(layout);
        return widgetForm;
    }

    // ===================== 1) STATEMENT ERFASSEN =====================

    /** Whether the create-statement action can be triggered. */
    canCreateStatement(): boolean {
        return !!this.statementPartnerId
            && !!this.statementDate
            && !!this.statementPeriodDate
            && this.statementState !== 'busy';
    }

    /**
    * Create the CommissionStatement from the action inputs (POST /cn/commission-statement).
    * On success the line-entry inputs and the Modell-Abgleich statement id are pre-filled.
    */
    createStatement(): void {
        if (!this.canCreateStatement()) {
            return;
        }

        this.statementState = 'busy';
        this.statementError = null;

        const period = this.periodString(this.statementPeriodDate)!;
        const request: CnCreateStatementRequestDto = {
            partnerId: this.statementPartnerId!,
            statementDate: this.toIsoDate(this.statementDate!),
            period
        };

        this.reconciliationApiService.createStatement(request).subscribe({
            next: (value) => {
                this.statement = value;
                this.statementLines = [];
                this.statementState = 'ready';
                // Pre-fill dependent action inputs from the created statement.
                this.lineBookingPeriod = period;
                this.abgleichStatementId = value?.id ?? null;
                this.runPartnerId = this.statementPartnerId;
                this.runPeriodDate = this.statementPeriodDate;
                // Surface the freshly created Beleg in the Beleg-Auswahl dropdown (Simone W5).
                if (value?.id != null) {
                    const label = this.buildStatementLabel(
                        value.id, value.partnerId ?? this.statementPartnerId ?? undefined, value.period ?? period
                    );
                    this.statementOptions = [
                        {label, value: value.id},
                        ...this.statementOptions.filter(o => o.value !== value.id)
                    ];
                }
            },
            error: (err) => {
                this.statementState = 'error';
                this.statementError = cnErrorMessage(err);
            }
        });
    }

    // ===================== 2) LINE HINZUFÜGEN =====================

    /** Whether a statement line can be appended (a statement must exist first). */
    canAddLine(): boolean {
        return !!this.statement?.id
            && !!this.lineTariffKey
            && !!this.lineBookingPeriod
            && this.lineAmount != null
            && this.lineTrigger != null
            && this.lineState !== 'busy';
    }

    /**
    * Append one statement line from the action inputs (POST /cn/commission-statement/{id}/line).
    * Received commissions (Neuvertrag / Storno) are entered via this UI.
    */
    addLine(): void {
        if (!this.canAddLine()) {
            return;
        }

        this.lineState = 'busy';
        this.lineError = null;

        const request: CnAddStatementLineRequestDto = {
            tariffKey: this.lineTariffKey!,
            bookingPeriod: this.lineBookingPeriod!,
            amount: this.lineAmount!,
            trigger: this.lineTrigger
        };
        if (this.lineVsnr) {
            request.vsnr = this.lineVsnr;
        }
        if (this.lineContractId != null) {
            request.contractId = this.lineContractId;
        }

        this.reconciliationApiService.addLine(this.statement!.id, request).subscribe({
            next: (value) => {
                this.statementLines = [...this.statementLines, value];
                this.lineState = 'idle';
                // Reset the per-line inputs (keep booking period + trigger for fast entry).
                this.lineVsnr = null;
                this.lineTariffKey = null;
                this.lineAmount = null;
                this.lineContractId = null;
            },
            error: (err) => {
                this.lineState = 'error';
                this.lineError = cnErrorMessage(err);
            }
        });
    }

    // ===================== 3) ABGLEICH STARTEN =====================

    /** Whether the reconciliation run can be triggered. */
    canRunReconciliation(): boolean {
        return !!this.runPartnerId && !!this.runPeriodDate && this.runState !== 'busy';
    }

    /**
    * Run the automated reconciliation (POST /cn/reconciliation/run) and render the result.
    * The per-status summary comes from the backend; the row table is a server-paged mvs-widget.
    */
    runReconciliation(): void {
        if (!this.canRunReconciliation()) {
            return;
        }

        this.runState = 'busy';
        this.runError = null;
        this.runResult = null;
        this.reconciliationWidget = null;
        this.componentWidget = null;

        const request: CnRunReconciliationRequestDto = {
            partnerId: this.runPartnerId!,
            period: this.periodString(this.runPeriodDate)!
        };

        this.reconciliationApiService.runReconciliation(request).subscribe({
            next: (value) => {
                this.runResult = value;
                this.runState = 'ready';
                this.refreshReconciliationWidget();
            },
            error: (err) => {
                this.runState = 'error';
                this.runError = cnErrorMessage(err);
            }
        });
    }

    /**
    * AI: Build / rebuild the server-paged reconciliation result table (master).
    * Status badges are rendered by mvs-widget from the backend enum uiColor — never hardcoded.
    * @widget cn.reconciliation.result.page (entity, list)
    */
    refreshReconciliationWidget(): void {
        this.reconciliationWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.reconciliation.result.page',
            'Abgleich-Ergebnisse',
            'cn.CommissionReconciliation',
            [],
            [new Sorting('e.id', false)]
        );
    }

    /**
    * Drill down from a reconciliation row to its components (detail), server-paged.
    * @widget cn.reconciliation.result.component.page (entity, list)
    */
    handleReconciliationSelect(object: ObjectIdentifierData | ObjectIdentifier): void {
        const data = object as ObjectIdentifierData;
        const reconciliationId = data?.objectId;
        if (!reconciliationId) {
            return;
        }
        const filters: FilterCriteria[] = [
            FilterCriteria.create(
                'e.reconciliationId', FilterCriteria.cOperatorEqual, reconciliationId
            )
        ];
        this.componentWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cn.reconciliation.result.component.page',
            'Abgleich-Komponenten',
            'cn.CommissionReconciliationComponent',
            filters,
            [new Sorting('e.id', false)]
        );
    }

    // ===================== 4) MODELL-ABGLEICH =====================

    /** Active model select handler (picker widget). */
    handleAbgleichActiveModelSelect(object: ObjectIdentifierData | ObjectIdentifier): void {
        const id = (object as ObjectIdentifierData)?.objectId;
        if (id) {
            this.abgleichActiveModelId = id;
        }
    }

    /** Scenario model multi-select handler (picker widget; toggles membership). */
    handleAbgleichScenarioModelSelect(object: ObjectIdentifierData | ObjectIdentifier): void {
        const id = (object as ObjectIdentifierData)?.objectId;
        if (!id) {
            return;
        }
        if (this.abgleichScenarioModelIds.includes(id)) {
            this.abgleichScenarioModelIds = this.abgleichScenarioModelIds.filter(x => x !== id);
        } else {
            this.abgleichScenarioModelIds = [...this.abgleichScenarioModelIds, id];
        }
    }

    /** Whether the Modell-Abgleich can be triggered. */
    canRunAbgleich(): boolean {
        return !!this.abgleichStatementId
            && !!this.abgleichActiveModelId
            && this.abgleichHorizonYears > 0;
    }

    /**
    * Open the Modell-Vergleich page on its Soll/Ist-Abgleich tab with the current selection. The
    * selection is passed via query params (statementId, activeModelId, horizonYears,
    * scenarioModelIds as a comma-separated string, tab=abgleich); the target page runs
    * POST /cn/reconciliation/abgleich and renders the rich result. The inline result was removed
    * from this panel — the comparison now lives on the dedicated tab.
    */
    runAbgleich(): void {
        if (!this.canRunAbgleich()) {
            return;
        }

        this.router.navigate(['/cn/model-comparison'], {
            queryParams: {
                tab: 'abgleich',
                statementId: this.abgleichStatementId,
                activeModelId: this.abgleichActiveModelId,
                horizonYears: this.abgleichHorizonYears,
                scenarioModelIds: this.abgleichScenarioModelIds.join(',')
            }
        });
    }

    /** ISO-8601 date (yyyy-MM-dd) from a Date, for the createStatement request. */
    private toIsoDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * FE-8: serialise a month-picker Date to a YearMonth "YYYY-MM" string for the API. Mirrors the
     * dashboard's period serialisation (cn-dashboard.page resolvePeriod) so both screens speak the
     * exact same period format to the backend. Returns null when no period is selected.
     */
    private periodString(date: Date | null): string | null {
        if (!date) {
            return null;
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    }
}
